import type { TFieldValue } from "../types/form.types";
import type { ISelectOption } from "../interface/form.interfaces";

// ─── Shared props ─────────────────────────────────────────────────────────────

export interface BaseInputProps {
  id: string;
  disabled?: boolean;
  onBlur: () => void;
  style?: React.CSSProperties;
}

// ─── TextInput ────────────────────────────────────────────────────────────────

interface TextInputProps extends BaseInputProps {
  type: "text" | "email" | "password" | "number" | "date";
  value: string | number;
  placeholder?: string;
  onChange: (value: TFieldValue) => void;
  hasError: boolean;
  rightElement?: React.ReactNode;
}

export const inputFocusStyles = {
  onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.setProperty("border", "1px solid orange", "important");
    e.currentTarget.style.setProperty("color", "Black", "important");
  },

  onBlur: (e: React.FocusEvent<HTMLInputElement>, originalOnBlur: () => void) => {
    e.currentTarget.style.setProperty("border", "1px solid orange", "important");
    e.currentTarget.style.setProperty("color", "black", "important");
    originalOnBlur();
  },
};
export function TextInput({ id, type, value, placeholder, disabled, hasError, onChange, onBlur, style, rightElement }: TextInputProps) {
  if (rightElement) {
    return (
      <div style={{ position: "relative", width: "100%", display: "flex", alignItems: "center" }}>
        <input
          id={id}
          type={type}
          className={`df-input${hasError ? " df-input--error" : ""}`}
          style={{ ...style, width: "100%", paddingRight: "40px" }}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)}
          onFocus={inputFocusStyles.onFocus} //  borde naranja
          onBlur={(e) => inputFocusStyles.onBlur(e, onBlur)}
        />
        <div style={{ position: "relative", right: "2rem", display: "flex", alignItems: "center" }}>{rightElement}</div>
      </div>
    );
  }

  return (
    <input
      id={id}
      type={type}
      className={`df-input${hasError ? " df-input--error" : ""}`}
      style={style}
      onFocus={inputFocusStyles.onFocus}
      onBlur={(e) => inputFocusStyles.onBlur(e, onBlur)}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)}
    />
  );
}

// ─── TextareaInput ────────────────────────────────────────────────────────────

interface TextareaInputProps extends BaseInputProps {
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  hasError: boolean;
}

export function TextareaInput({ id, value, placeholder, disabled, hasError, onChange, onBlur, style }: TextareaInputProps) {
  return (
    <textarea
      id={id}
      className={`df-input${hasError ? " df-input--error" : ""}`}
      style={style}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      onFocus={(e) => e.currentTarget.style.setProperty("border", "1px solid orange", "important")}
    />
  );
}

// ─── SelectInput ──────────────────────────────────────────────────────────────

interface SelectInputProps extends BaseInputProps {
  value: string;
  options: ISelectOption[];
  onChange: (value: string) => void;
  hasError: boolean;
}

export function SelectInput({ id, value, options, disabled, hasError, onChange, onBlur, style }: SelectInputProps) {
  return (
    <select
      id={id}
      className={`df-input${hasError ? " df-input--error" : ""}`}
      style={style}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      onFocus={(e) => e.currentTarget.style.setProperty("border", "1px solid orange", "important")}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

// ─── RadioGroup ───────────────────────────────────────────────────────────────

interface RadioGroupProps {
  name: string;
  value: string;
  options: ISelectOption[];
  disabled?: boolean;
  onChange: (value: string) => void;
  containerStyle?: React.CSSProperties;
  optionStyle?: React.CSSProperties;
}

export function RadioGroup({ name, value, options, disabled, onChange, containerStyle, optionStyle }: RadioGroupProps) {
  return (
    <div role="radiogroup" aria-labelledby={`${name}-label`} style={containerStyle}>
      {options.map((opt) => (
        <label key={opt.value} className="df-option" style={optionStyle}>
          <input type="radio" name={name} value={opt.value} checked={value === opt.value} disabled={disabled} onChange={() => onChange(opt.value)} />
          {opt.label}
        </label>
      ))}
    </div>
  );
}

// ─── CheckboxInput ────────────────────────────────────────────────────────────

interface CheckboxInputProps {
  id: string;
  label: string;
  checked: boolean;
  required?: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  style?: React.CSSProperties;
}

export function CheckboxInput({ id, label, checked, required, disabled, onChange, style }: CheckboxInputProps) {
  return (
    <label className="df-option" style={style}>
      <input id={id} type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      {label}
      {required && (
        <span className="df-required" aria-hidden="true">
          *
        </span>
      )}
    </label>
  );
}
